import React, { useState, useCallback } from "react";

import { translateText } from "../utils/translate";
import styles from "./styles.module.scss";
import { Card } from "@radix-ui/themes";

export function Translation() {
  const [sourceText, setSourceText] = useState("");
  const [targetLang, setTargetLang] = useState("en");
  const [translatedText, setTranslatedText] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handlePaste = useCallback(function (
    e: React.ClipboardEvent<HTMLTextAreaElement>
  ) {
    setSourceText(e.clipboardData.getData("Text"));
  },
  []);

  const handleTranslate = useCallback(
    async function () {
      setIsTranslating(true);
      setError(null);
      try {
        let translation: string;
        try {
          translation = await (translateText.length === 2
            ? // @ts-ignore
              translateText(sourceText, targetLang)
            : translateText(sourceText, "en"));
        } catch (err) {
          translation = await translateText(sourceText, "en");
        }
        setTranslatedText(translation);
      } catch (err) {
        setError("Failed to translate text.");
        // log error
        // eslint-disable-next-line no-console
        console.error("translation error:", err);
      } finally {
        setIsTranslating(false);
      }
    },
    [sourceText, targetLang]
  );

  const handleSourceTextChange = useCallback(function (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) {
    setSourceText(e.target.value);
  },
  []);

  const handleTargetLangChange = useCallback(function (
    e: React.ChangeEvent<HTMLSelectElement>
  ) {
    setTargetLang(e.target.value);
  },
  []);

  const handleCopy = useCallback(
    async function () {
      if (translatedText) {
        try {
          await navigator.clipboard.writeText(translatedText);
          setCopied(true);
          setTimeout(function () {
            setCopied(false);
          }, 1200);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("copy failed:", err);
        }
      }
    },
    [translatedText]
  );

  return (
    <Card className="w-full mx-auto">
      <div className={styles.container}>
        <div className={styles.inputGroup}>
          <textarea
            className={styles.textarea}
            rows={3}
            placeholder="Paste or type text to translate..."
            value={sourceText}
            onChange={handleSourceTextChange}
            onPaste={handlePaste}
          />
          <select
            className={styles.select}
            value={targetLang}
            onChange={handleTargetLangChange}
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
            <option value="it">Italian</option>
            <option value="pt">Portuguese</option>
            <option value="zh">Chinese (Simplified)</option>
            <option value="zh-TW">Chinese (Traditional)</option>
            <option value="ja">Japanese</option>
            <option value="ar">Arabic</option>
            <option value="ru">Russian</option>
            <option value="nl">Dutch</option>
            <option value="sv">Swedish</option>
            <option value="no">Norwegian</option>
            <option value="da">Danish</option>
            <option value="fi">Finnish</option>
            <option value="pl">Polish</option>
            <option value="tr">Turkish</option>
            <option value="he">Hebrew</option>
            <option value="el">Greek</option>
            <option value="cs">Czech</option>
            <option value="hu">Hungarian</option>
            <option value="ro">Romanian</option>
            <option value="th">Thai</option>
            <option value="vi">Vietnamese</option>
            <option value="id">Indonesian</option>
            <option value="hi">Hindi</option>
            <option value="uk">Ukrainian</option>
            <option value="bg">Bulgarian</option>
            <option value="hr">Croatian</option>
            <option value="sk">Slovak</option>
            <option value="sl">Slovenian</option>
            <option value="ms">Malay</option>
            <option value="fa">Persian</option>
            <option value="ta">Tamil</option>
            <option value="bn">Bengali</option>
            <option value="sw">Swahili</option>
          </select>
          <button
            className={styles.translateButton}
            onClick={handleTranslate}
            disabled={isTranslating || !sourceText.trim()}
            type="button"
          >
            {isTranslating ? "Translating..." : "Translate"}
          </button>
        </div>
        <div
          className={styles.resultContainer}
          style={{ position: "relative" }}
        >
          <p className={styles.resultText}>
            {translatedText
              ? translatedText
              : "Translated content will appear here..."}
          </p>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!translatedText}
            style={{
              position: "absolute",
              bottom: "1rem",
              right: "1rem",
              background: copied ? "#d1fae5" : "#f3f4f6",
              border: "none",
              borderRadius: "0.375rem",
              padding: "0.25rem 0.75rem",
              cursor: translatedText ? "pointer" : "not-allowed",
              fontSize: "0.875rem",
              color: copied ? "#047857" : "#374151",
              opacity: translatedText ? 1 : 0.5,
              transition: "background 0.2s, color 0.2s",
            }}
            aria-label="Copy translation"
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        {error && <div className={styles.errorText}>{error}</div>}
      </div>
    </Card>
  );
}
